"use client";

/**
 * About.tsx
 *
 * This section provides a warm welcome and brief description of the restaurant's vibe.
 *
 * Key features:
 * - Dynamic CMS-driven text (title, subtitle, description)
 * - A grid of "highlights" or features (usually three items)
 * - Safe data parsing to ensure CMS glitches don't break the layout
 * - Support for broken image fallbacks
 */

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useAppSelector } from "@/store/hooks";
import DiamondDivider from "../shared/DiamondDivider";

interface Feature {
  image: string;
  imageSrc?: string;
  title: string;
  label?: string;
}

const DEFAULT_HIGHLIGHTS: Feature[] = [
  {
    image: "/themes/royal-blue/about-1.png",
    title: "Start Eating Better",
  },
  {
    image: "/themes/royal-blue/about-2.png",
    title: "Quality Is The Heart",
  },
  {
    image: "/themes/royal-blue/about-3.png",
    title: "Hot & Ready To Serve",
  },
];

// ---------------------------------------------------------------------------
// Data Validation
// ---------------------------------------------------------------------------

/**
 * Ensures the highlights array is valid and only contains items with
 * a proper title and image. Returns defaults if CMS data is missing.
 */
function getSafeHighlights(raw: any): Feature[] {
  try {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_HIGHLIGHTS;

    const validated = raw.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (typeof item.title === "string" || typeof item.label === "string") &&
        (typeof item.image === "string" || typeof item.imageSrc === "string"),
    );

    return validated.length > 0 ? validated : DEFAULT_HIGHLIGHTS;
  } catch {
    return DEFAULT_HIGHLIGHTS;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const About = () => {
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.about;

  const highlights = getSafeHighlights(data?.highlights);

  return (
    <section id="about" className="py-24 bg-white text-center">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* ── Subtitle ── */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-sacramento text-4xl text-[#c59d5f] mb-4"
        >
          {data?.subtitle || "Welcome To The Royal Blue"}
        </motion.p>

        {/* ── Heading ── */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-cormorant text-4xl md:text-5xl lg:text-[46px] text-[#1a1a1a] mb-8 font-bold leading-tight max-w-4xl mx-auto"
        >
          {data?.title ||
            "Delicious Food, Friendly Staff, Cozy Atmosphere & Positive Emotions!"}
        </motion.h2>

        {/* ── Divider ── */}
        <DiamondDivider className="mb-20" />

        {/* ── Main Feature Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 px-4 md:px-12 lg:px-24">
          {highlights.map((feature: any, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative aspect-[3/4] group overflow-hidden cursor-pointer"
            >
              <Image
                src={
                  feature.imageSrc ||
                  feature.image ||
                  "/themes/royal-blue/about-1.png"
                }
                alt={feature.label || feature.title || ""}
                fill
                unoptimized
                className="object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "/themes/royal-blue/about-1.png";
                }}
              />
              {/* Overlay + Title */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300"></div>
              <div className="absolute inset-x-0 bottom-10 flex flex-col items-center">
                <span className="font-playfair italic text-3xl text-white drop-shadow-lg text-center px-4">
                  {feature.label || feature.title}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Description Summary ── */}
        <div className="max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="text-[17px] text-[#777777] leading-relaxed mb-6 font-inter"
          >
            {data?.description ||
              "Royal Blue was the first restaurant to open in UK, the restaurant was designed with the history in mind we have created a soft industrial dining room."}
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default About;
